import { StoreApi } from 'zustand';
import React, { createElement } from 'react';
import { useStoreWithEqualityFn } from 'zustand/traditional';

/**
 * Zustand の Store から State を抽出するための型推論値。
 */
type ExtractState<Store> = Store extends { getState: () => infer T } ? T : never;

/**
 * Zustand を使った DI コンテキストを作成
 */
export const createDI = <State, Store extends StoreApi<State> = StoreApi<State>>() => {
  const StoreContext = React.createContext<Store | undefined>(undefined);

  type Provider = React.FC<
    {
      storeCreator: () => Store;
      children: React.ReactNode;
    } & Record<string, unknown>
  >;

  const provider: Provider = ({ storeCreator, ...props }) => {
    const storeRef = React.useRef<Store>();
    const results = createElement(StoreContext.Provider, {
      value: (storeRef.current ||= storeCreator()),
      ...props,
    });
    return results;
  };

  const useStore = <StateSlice = ExtractState<Store>>(
    selector: (state: ExtractState<Store>) => StateSlice,
    equalityFn?: (left: StateSlice, right: StateSlice) => boolean,
  ) => {
    const store = React.useContext(StoreContext);
    if (store) {
      return useStoreWithEqualityFn(store, selector, equalityFn);
    }
    throw new Error('この処理は zustand の Provider 内でのみ利用できます');
  };

  return [provider, useStore] as const;
};
